import { FC, PropsWithChildren, useMemo } from 'react';
import styled from 'styled-components';
import { Box, Stack, Typography } from '@mui/material';
import Markdown from 'markdown-to-jsx';
import { format } from 'date-fns';
import {
  UserAvatar,
  textOutput,
  markdownOptions,
} from '@vs-online-only-app/ui';
import {
  HeroConversationMessage,
  ConversationMessageType,
} from '@vs-online-only-app/state';
import useConversationMessage from '../../hooks/useConversationMessage';
import { useSelectedHeroConversation } from '../../hooks/useSelectedHeroConversation';
import ChatBubble from './ChatBubble';
import EmojiReaction from './EmojiReaction';

type Props = {
  message: HeroConversationMessage;
};

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: ${({ theme }) => theme.spacing(4)} 1fr;
  column-gap: ${({ theme }) => theme.spacing(1)};
  align-items: flex-start;
`;

const ParticipantMessage: FC<PropsWithChildren<Props>> = ({
  message,
  children,
}) => {
  const { variant, isFirstInStack, isStacked, isLastInStack } =
    useConversationMessage(message);

  const maxWidthPercentage = ((variant === 'outgoing' ? 9 : 10) / 12) * 100;
  const heroConversation = useSelectedHeroConversation();
  const conversationStarter = heroConversation?.conversationStarter;

  const isTextMessage =
    message.attributes.type === ConversationMessageType.Text ||
    message.attributes.type === ConversationMessageType.OutboundText ||
    message.attributes.type === ConversationMessageType.OutboundTextOnly ||
    message.attributes.type === ConversationMessageType.Email;

  const backgroundColor = useMemo(() => {
    if (!isTextMessage) {
      return undefined;
    }
    if (variant === 'outgoing') {
      return 'primary';
    }
    return 'secondary';
  }, [variant, isTextMessage]);

  const borderColor = useMemo(() => {
    if (isTextMessage) {
      return undefined;
    }
    if (variant === 'outgoing') {
      return 'primary';
    }
    return 'secondary';
  }, [variant, isTextMessage]);

  const reaction = message.attributes?.data?.reaction;
  const direction = variant === 'incoming' ? 'left' : 'right';
  const text = (children && message.body) ?? textOutput(message.body);

  return (
    <GridWrapper>
      {variant === 'incoming' && (isFirstInStack || !isStacked) && (
        <UserAvatar
          color={
            heroConversation?.archiveAttributes ? undefined : '#E95D11' // Bambuser orange
          }
          noPadding
          fallbackCharacter={conversationStarter?.firstName
            .charAt(0)
            .toUpperCase()}
        />
      )}
      <Box sx={{ gridColumn: variant === 'incoming' ? '2/3' : '1/3' }}>
        <ChatBubble
          maxWidthPercentage={maxWidthPercentage}
          direction={direction}
          isLastInStack={isLastInStack}
          isFirstInStack={isFirstInStack}
          isStacked={isStacked}
          backgroundColor={backgroundColor}
          borderColor={borderColor}
        >
          <Stack
            justifyContent="space-between"
            alignItems="flex-end"
            direction="row"
            gap={1}
          >
            <Typography
              sx={{ wordBreak: 'break-word' }}
              variant="body2"
              component="span"
            >
              {isTextMessage ? (
                <Markdown options={markdownOptions}>{String(text)}</Markdown>
              ) : (
                (children ?? message.body)
              )}
            </Typography>
            {message?.dateCreated && (
              <Typography variant="caption" sx={{ opacity: 0.5 }}>
                {format(message.dateCreated, 'HH:mm')}
              </Typography>
            )}
          </Stack>
          {reaction && (
            <EmojiReaction reaction={reaction} direction={direction} />
          )}
        </ChatBubble>
      </Box>
    </GridWrapper>
  );
};

export default ParticipantMessage;
